import { PRICE_AXIS_MIN_WIDTH_PX } from "./constants";
import { syncViewWithBars } from "./syncViewWithBars";
import { createRenderScheduler } from "./renderScheduler";
import { createAnimator } from "./animator";
import { observeSize } from "./sizeObserver";
import { readTheme } from "./theme";
import { drawFrame } from "./drawFrame";
import { createLayers } from "./createLayers";
import { createPositionsState } from "./overlays/positions/positionsState";
import TimeScale from "./scales/TimeScale";
import PriceScale from "./scales/PriceScale";
import { createSecondsFirstView } from "./scales/secondsFirstView";
import { scrollToEndView } from "./scales/newBarScroll";
import { visibleSpanView } from "./scales/visibleSpan";
import DataController from "./data/DataController";
import TimeAxis from "./axes/TimeAxis";
import { attachInteraction } from "./interaction/attachInteraction";
import { createViewEvents } from "./viewEvents";
import { createIndicatorApi } from "./indicators/indicatorApi";
import { createDrawingApi } from "./drawings/drawingApi";

/**
 * Chart engine: WebGL2 layer for geometry, Canvas 2D layers for text and for
 * the crosshair inside `container`, all TradingView gestures. Knows nothing
 * about React or Redux: data comes from the datafeed, settings through
 * methods, open positions through `positions` (positionsState.js),
 * indicators through addIndicator & co. (indicators/indicatorApi.js), drawings
 * through addDrawing & co. (drawings/drawingApi.js), changes of the view and
 * of those lists go out through `events` (viewEvents.js).
 * A frame is drawn only after a change or while the animator moves something.
 */
export default class ChartEngine {
  /**
   * @param {HTMLElement} container - positioned element; colours come from its CSS custom properties
   * @param {{ datafeed: object, drawingsStorage: object }} options - datafeed from createDatafeed();
   *   drawingsStorage: where the drawings of a symbol are kept (settings/savedDrawings.js)
   */
  constructor(container, { datafeed, drawingsStorage }) {
    this.isDestroyed = false;
    this.drawingsStorage = drawingsStorage;
    this.size = null;
    this.priceAxisWidth = PRICE_AXIS_MIN_WIDTH_PX;
    this.timeScale = new TimeScale();
    this.priceScale = new PriceScale();
    this.timeAxis = new TimeAxis();
    this.secondsFirstView = createSecondsFirstView();
    this.toggles = { timerEnabled: true, autoscrollEnabled: true, gridSnapEnabled: false };
    this.theme = readTheme(container);
    this.scheduler = createRenderScheduler(() => this.renderFrame());
    this.positions = createPositionsState({
      invalidate: () => this.invalidate(),
      redrawText: () => this.textFrame.redraw(),
    });
    Object.assign(this, createLayers(this, container));
    this.animator = createAnimator(this);
    this.data = new DataController(datafeed, (change) => {
      syncViewWithBars(change, this);
      this.invalidate();
    });
    this.events = {
      ...createViewEvents(),
      onIndicatorsChange: this.indicators.subscribe,
      onDrawingsChange: this.drawings.subscribe,
    };
    Object.assign(this, createIndicatorApi(this), createDrawingApi(this));
    this.interaction = attachInteraction(this, container);
    this.stopObservingSize = observeSize(container, (size) => this.resize(size));
  }

  /**
   * @param {string} symbol - pair slug ("BTCUSDT"): new history, stream and drawings, the zoom stays
   */
  setSymbol(symbol) {
    if (symbol === this.data.symbol) return;

    // What the pointer holds belongs to the old pair and ends before its drawings are saved
    this.interaction.cancelGesture();
    this.drawings.setSymbol(symbol);
    this.timeScale.showLastBar();
    this.data.setSymbol(symbol);
  }

  /** @param {string} resolution - TV resolution ("1", "60", "D", "15S"), like setSymbol */
  setResolution(resolution) {
    if (resolution === this.data.resolution) return;

    this.timeScale.showLastBar();
    this.data.setResolution(resolution);
  }

  /** @param {number} type - CHART_TYPE_* from FuturesChart/constants.js */
  setChartType(type) {
    this.series.setType(type);
    this.invalidate();
  }

  /** @param {{ upColor: string, downColor: string, shadowStyle: string }} style - CSS colours, SHADOW_STYLE value */
  setSeriesStyle(style) {
    this.series.setStyle(style);
    this.invalidate();
  }

  /** @param {{ timerEnabled: boolean, autoscrollEnabled: boolean, gridSnapEnabled: boolean }} toggles - autoscroll: newBarScroll.js; grid snap: the crosshair (overlays/crosshair.js) */
  setToggles({ timerEnabled, autoscrollEnabled, gridSnapEnabled }) {
    this.toggles = { timerEnabled, autoscrollEnabled, gridSnapEnabled };
    this.invalidate();
  }

  /** Reloads the history and resubscribes, e.g. after the tab slept; the view stays. */
  reloadData() {
    this.data.reload();
  }

  /**
   * @param {number} seconds - shown across the pane, ending with the latest bars; animated (E5)
   */
  setVisibleSpan(seconds) {
    const view = visibleSpanView(this.timeScale, seconds, this.data.barDurationMs);

    if (view) this.animator.moveViewTo(view);
  }

  /** Slides back to the latest bars, the zoom stays (the «›» button). */
  scrollToRealtime() {
    this.animator.moveViewTo(scrollToEndView(this.timeScale));
  }

  /**
   * @param {string} timezone - IANA zone of the time axis
   * @param {string|null} locale - language of month names
   */
  setTimezone(timezone, locale) {
    this.timeAxis.setTimezone(timezone, locale);
    this.invalidate();
  }

  /**
   * Schedules a frame. After every frame the visible range is checked and
   * older history is requested near the left edge, so pan and zoom only
   * need to change the time scale and call this.
   */
  invalidate() {
    if (!this.isDestroyed) this.scheduler.invalidate();
  }

  destroy() {
    this.isDestroyed = true;
    this.scheduler.destroy();
    this.animator.destroy();
    this.data.destroy();
    this.interaction.destroy();
    this.stopObservingSize();
    this.destroyLayers();
  }

  resize(size) {
    this.size = size;
    this.glLayer.resize(size);
    this.textLayer.resize(size);
    this.interaction.resize(size);
    // Resizing clears the canvases, so the frame is drawn right away, without a blank flash
    this.renderFrame();
  }

  renderFrame() {
    if (!this.size || this.isDestroyed) return;

    const frame = drawFrame(this);

    this.textFrame.draw(frame);
    this.priceAxisWidth = frame.priceAxisWidth;
    this.data.handleVisibleRange(frame.range);
    this.interaction.afterFrame(frame);
  }
}
